import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  syncVisualViewportHeight,
  useLiveTrainingPortalRoot,
} from "../../hooks/useLiveFullscreen";
import { useOrientation } from "../../hooks/useOrientation";
import {
  AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
  CAMERA_VIEW_OPTIONS,
  DEFAULT_CLIENT_SETTINGS,
  DEFAULT_COACH_SESSION_SETTINGS,
  RECORDING_RESOLUTION_OPTIONS,
  REPLAY_FPS_OPTIONS,
  REPLAY_VIDEO_QUALITY_OPTIONS,
  SEQUENCE_PAUSE_SEC_OPTIONS,
  VIDEO_QUALITY_OPTIONS,
  getEffectiveClientCaptureSettings,
  loadClientSettings,
  loadCoachSessionSettings,
  loadForcedConstraints,
  normalizeClientSettings,
  normalizeCoachSessionSettings,
  publishLiveTrainingClientConstraints,
  recordingFpsOptionsForLicense,
  saveClientSettings,
  saveCoachSessionSettings,
} from "../../utils/liveTrainingSettings";
import { AUTO_SEQUENCE_TRANSITION_PAUSE_SEC } from "../../utils/sequenceTransitionPause";
import { useClubLicense } from "../../context/ClubLicenseContext";
import { useBrand } from "../../context/BrandContext";
import { filterTablesForStation } from "../../utils/coachSessionTables";
import { listTablesWithSeatedPlayers } from "../../utils/liveTrainingPlayerContext";
import { StationRecordingRoster } from "./StationRecordingRoster";
import {
  normalizeTablePhoneCoverage,
  setTablePhoneMainTable,
  setTablePhoneTables,
  tableNumbersFromSessionTables,
  tablePhoneCoveredTables,
  toggleTablePhoneTable,
} from "../../utils/tablePhoneCoverage";
import "./LiveTrainingSettingsDialog.css";

/**
 * Live Training settings — coach session prefs + this-device client prefs.
 * When the coach enables force, connected clients lock FPS / autoeval duration.
 */
export function LiveTrainingSettingsDialog({
  open,
  onClose,
  tsId = null,
  isCoachView = false,
  sessionTables = [],
  stations = [],
  roster = [],
  exercise = null,
  socket = null,
  t = (k, d) => d || k,
  onSaved,
}) {
  const { features } = useClubLicense();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";
  const portalRoot = useLiveTrainingPortalRoot();
  const orientation = useOrientation();
  const isLandscape = orientation === "landscape";
  const fpsOptions = recordingFpsOptionsForLicense(features);
  const [coachDraft, setCoachDraft] = useState(() =>
    loadCoachSessionSettings(tsId)
  );
  const [clientDraft, setClientDraft] = useState(() => loadClientSettings());
  const [forced, setForced] = useState(() => loadForcedConstraints(tsId));
  const stationRecordingTables = useMemo(() => {
    if (clientDraft.stationPhoneEnabled !== true || !clientDraft.stationPhoneTsgId) {
      return [];
    }
    return listTablesWithSeatedPlayers(
      filterTablesForStation(
        sessionTables,
        clientDraft.stationPhoneTsgId,
        stations
      ),
      exercise,
      roster,
      null,
      null
    );
  }, [
    clientDraft.stationPhoneEnabled,
    clientDraft.stationPhoneTsgId,
    sessionTables,
    stations,
    exercise,
    roster,
  ]);

  useEffect(() => {
    if (!open) return;
    setCoachDraft(loadCoachSessionSettings(tsId));
    setClientDraft(loadClientSettings());
    setForced(loadForcedConstraints(tsId));
    syncVisualViewportHeight();
  }, [open, tsId]);

  useEffect(() => {
    if (!open) return undefined;
    const onViewport = () => syncVisualViewportHeight();
    onViewport();
    window.addEventListener("resize", onViewport);
    window.addEventListener("orientationchange", onViewport);
    window.visualViewport?.addEventListener("resize", onViewport);
    window.visualViewport?.addEventListener("scroll", onViewport);
    return () => {
      window.removeEventListener("resize", onViewport);
      window.removeEventListener("orientationchange", onViewport);
      window.visualViewport?.removeEventListener("resize", onViewport);
      window.visualViewport?.removeEventListener("scroll", onViewport);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onForced = (e) => {
      if (String(e?.detail?.tsId) !== String(tsId)) return;
      setForced(e.detail.constraints || loadForcedConstraints(tsId));
    };
    window.addEventListener("smarttt:live-training-forced-constraints", onForced);
    return () =>
      window.removeEventListener(
        "smarttt:live-training-forced-constraints",
        onForced
      );
  }, [open, tsId]);

  const sessionTableNumbers = useMemo(
    () => tableNumbersFromSessionTables(sessionTables),
    [sessionTables]
  );
  const coveredTables = tablePhoneCoveredTables(clientDraft);
  const mainTable = normalizeTablePhoneCoverage(
    clientDraft.tablePhoneNumber,
    coveredTables
  ).main;
  const allTablesCovered =
    sessionTableNumbers.length > 0 &&
    sessionTableNumbers.every((n) => coveredTables.includes(n));

  const effective = getEffectiveClientCaptureSettings(tsId);
  const clientLocked =
    !isCoachView &&
    (forced?.forceClientCaptureSettings === true || effective.forced);
  const durationLocked = Boolean(forced) || clientLocked;

  const handleSave = () => {
    let coachSaved = null;
    let clientSaved = null;
    if (isCoachView && tsId) {
      coachSaved = saveCoachSessionSettings(tsId, coachDraft);
      publishLiveTrainingClientConstraints(socket, tsId, coachSaved);
    }
    // Always persist device prefs (camera on/off, position). Forced coach
    // FPS/quality still win at read time via getEffectiveClientCaptureSettings.
    clientSaved = saveClientSettings(clientDraft);
    onSaved?.({ coach: coachSaved, client: clientSaved });
    onClose?.();
  };

  const handleReset = () => {
    if (isCoachView) {
      setCoachDraft({ ...DEFAULT_COACH_SESSION_SETTINGS });
    }
    if (!clientLocked) {
      setClientDraft({ ...DEFAULT_CLIENT_SETTINGS });
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      className={`lt-settings-dialog${
        isLandscape ? " lt-settings-dialog--landscape" : ""
      }`}
      aria-labelledby="lt-settings-title"
      container={portalRoot || undefined}
      disableScrollLock
      PaperProps={{
        className: "lt-settings-dialog__paper",
        elevation: 0,
        style: {
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
        },
      }}
      style={{
        "--lt-brand": brandPrimary,
        "--lt-brand-secondary": brandSecondary,
      }}
      sx={{ zIndex: 13000 }}
    >
      <DialogTitle id="lt-settings-title" className="lt-settings-dialog__title">
        {t("liveTrainingSettings", "Live Training settings")}
        <span className="lt-settings-dialog__subtitle">
          {t(
            "liveTrainingSettingsSubtitle",
            "Coach options apply on the next exercise start. Forced capture settings lock connected player devices."
          )}
        </span>
      </DialogTitle>
      <DialogContent className="lt-settings-dialog__content" dividers={false}>
        {isCoachView ? (
          <section className="lt-settings-dialog__section" aria-labelledby="lt-settings-coach">
            <h3 id="lt-settings-coach" className="lt-settings-dialog__section-title">
              {t("liveTrainingSettingsCoach", "Coach — session")}
            </h3>

            <div className="lt-settings-dialog__subsection">
              <h4 className="lt-settings-dialog__subsection-title">
                {t("liveTrainingSettingsSessionFlow", "Session flow")}
              </h4>
            <FormControlLabel
              className="lt-settings-dialog__switch"
              control={
                <Switch
                  checked={coachDraft.autoAdvanceSequences}
                  onChange={(_, checked) =>
                    setCoachDraft((prev) =>
                      normalizeCoachSessionSettings({
                        ...prev,
                        autoAdvanceSequences: checked,
                      })
                    )
                  }
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>
                    {t("autoAdvanceSequences", "Auto-start sequences")}
                  </strong>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "autoAdvanceSequencesHint",
                      `When on, each following block starts automatically after a ${AUTO_SEQUENCE_TRANSITION_PAUSE_SEC}-second pause. When off, start the next block from Exercise Control. Total time pauses between blocks.`
                    )}
                  </span>
                </span>
              }
            />

            <label
              className={`lt-settings-dialog__field${
                !coachDraft.autoAdvanceSequences
                  ? " lt-settings-dialog__field--disabled"
                  : ""
              }`}
              htmlFor="lt-settings-pause-sec"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "sequenceTransitionPauseDuration",
                  "Pause between sequences"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "sequenceTransitionPauseDurationHint",
                  "Rest time before the next block starts automatically. Default 15 seconds."
                )}
              </span>
              <select
                id="lt-settings-pause-sec"
                className="lt-settings-dialog__select"
                disabled={!coachDraft.autoAdvanceSequences}
                value={String(coachDraft.sequenceTransitionPauseSec)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      sequenceTransitionPauseSec: v,
                    })
                  );
                }}
              >
                {SEQUENCE_PAUSE_SEC_OPTIONS.map((opt) => (
                  <option key={opt} value={String(opt)}>
                    {opt}s
                  </option>
                ))}
              </select>
            </label>
            </div>

            <div className="lt-settings-dialog__subsection">
              <h4 className="lt-settings-dialog__subsection-title">
                {t("liveTrainingSettingsReplay", "Instant replay")}
              </h4>
            <FormControlLabel
              className="lt-settings-dialog__switch"
              control={
                <Switch
                  checked={coachDraft.instantReplayEnabled !== false}
                  onChange={(_, checked) =>
                    setCoachDraft((prev) =>
                      normalizeCoachSessionSettings({
                        ...prev,
                        instantReplayEnabled: checked,
                      })
                    )
                  }
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>
                    {t(
                      "liveTrainingInstantReplay",
                      "Last 10 seconds replay"
                    )}
                  </strong>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "liveTrainingInstantReplayHint",
                      "On by default. Player cameras stay warm and keep the last 10 seconds so you can replay a table or player during live training."
                    )}
                  </span>
                </span>
              }
            />
            <div className="lt-settings-dialog__fields-row">
            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-replay-fps"
            >
              <span className="lt-settings-dialog__label">
                {t("liveTrainingReplayRecordingFps", "Replay FPS")}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingReplayRecordingFpsHint",
                  "Frame rate for the last-10s replay ring. Independent of evaluation recording FPS."
                )}
              </span>
              <select
                id="lt-settings-coach-replay-fps"
                className="lt-settings-dialog__select"
                value={String(coachDraft.replayRecordingFps || 60)}
                onChange={(e) => {
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      replayRecordingFps: Number(e.target.value),
                    })
                  );
                }}
              >
                {REPLAY_FPS_OPTIONS.map((opt) => (
                  <option key={opt} value={String(opt)}>
                    {opt} fps
                  </option>
                ))}
              </select>
            </label>

            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-resolution"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingRecordingResolution",
                  "Replay resolution"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingRecordingResolutionHint",
                  "Frame size for the last-10s replay. Default 480p for a fast upload."
                )}
              </span>
              <select
                id="lt-settings-coach-resolution"
                className="lt-settings-dialog__select"
                value={String(coachDraft.recordingResolution || "480p")}
                onChange={(e) => {
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      recordingResolution: e.target.value,
                    })
                  );
                }}
              >
                {RECORDING_RESOLUTION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>

            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-replay-quality"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingReplayVideoQuality",
                  "Replay bitrate"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingReplayVideoQualityHint",
                  "Encode bitrate for the last-10s replay. Low uploads faster; medium looks sharper."
                )}
              </span>
              <select
                id="lt-settings-coach-replay-quality"
                className="lt-settings-dialog__select"
                value={String(coachDraft.replayVideoQuality || "low")}
                onChange={(e) => {
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      replayVideoQuality: e.target.value,
                    })
                  );
                }}
              >
                {REPLAY_VIDEO_QUALITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "medium"
                      ? t("liveTrainingVideoQualityMedium", "Medium")
                      : t("liveTrainingVideoQualityLow", "Low (faster upload)")}
                  </option>
                ))}
              </select>
            </label>
            </div>
            </div>

            <div className="lt-settings-dialog__subsection">
              <h4 className="lt-settings-dialog__subsection-title">
                {t("liveTrainingSettingsEvalCapture", "Evaluation capture")}
              </h4>
            <FormControlLabel
              className="lt-settings-dialog__switch"
              control={
                <Switch
                  checked={coachDraft.evalVideoRecordingEnabled !== false}
                  onChange={(_, checked) =>
                    setCoachDraft((prev) =>
                      normalizeCoachSessionSettings({
                        ...prev,
                        evalVideoRecordingEnabled: checked,
                      })
                    )
                  }
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>
                    {t(
                      "liveTrainingEvalVideoRecording",
                      "Record evaluation videos on players"
                    )}
                  </strong>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "liveTrainingEvalVideoRecordingHint",
                      "When on, invited player phones record the last auto-evaluation window (and KoT matches) as 720p H.264. Turn off to skip player clips for this session."
                    )}
                  </span>
                </span>
              }
            />

            <FormControlLabel
              className="lt-settings-dialog__switch"
              control={
                <Switch
                  checked={coachDraft.coachDeviceEvalRecordingEnabled === true}
                  onChange={(_, checked) =>
                    setCoachDraft((prev) =>
                      normalizeCoachSessionSettings({
                        ...prev,
                        coachDeviceEvalRecordingEnabled: checked,
                      })
                    )
                  }
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>
                    {t(
                      "liveTrainingCoachDeviceRecording",
                      "Record on this coach phone"
                    )}
                  </strong>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "liveTrainingCoachDeviceRecordingHint",
                      "Off by default. Enable only if you want this coach device to capture evaluation video as well."
                    )}
                  </span>
                </span>
              }
            />

            <FormControlLabel
              className="lt-settings-dialog__switch"
              control={
                <Switch
                  checked={coachDraft.forceClientCaptureSettings}
                  onChange={(_, checked) =>
                    setCoachDraft((prev) =>
                      normalizeCoachSessionSettings({
                        ...prev,
                        forceClientCaptureSettings: checked,
                      })
                    )
                  }
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>
                    {t(
                      "liveTrainingForceClientCapture",
                      "Force capture settings on clients"
                    )}
                  </strong>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "liveTrainingForceClientCaptureHint",
                      "When on, connected players must use the FPS and video quality below. Auto-evaluation clip duration always follows this session setting."
                    )}
                  </span>
                </span>
              }
            />

            <div className="lt-settings-dialog__fields-row">
            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-fps"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingForcedRecordingFps",
                  "Recording FPS for clients"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingForcedRecordingFpsHint",
                  "Default 60 fps for 60s evaluation clips. 30 and 60 are always available; 120 fps requires Pro."
                )}
              </span>
              <select
                id="lt-settings-coach-fps"
                className="lt-settings-dialog__select"
                value={String(coachDraft.recommendedRecordingFps)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      recommendedRecordingFps: v,
                    })
                  );
                }}
              >
                {fpsOptions.map((opt) => (
                  <option key={opt} value={String(opt)}>
                    {opt} fps
                  </option>
                ))}
              </select>
            </label>

            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-video-quality"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingForcedVideoQuality",
                  "Evaluation video quality for clients"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingForcedVideoQualityHint",
                  "H.264 upload size/quality on player devices when force is enabled. Low ≈ 8 MB per minute (faster upload)."
                )}
              </span>
              <select
                id="lt-settings-coach-video-quality"
                className="lt-settings-dialog__select"
                value={String(coachDraft.forcedVideoQuality || "low")}
                onChange={(e) => {
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      forcedVideoQuality: e.target.value,
                    })
                  );
                }}
              >
                <option value="low">
                  {t("liveTrainingVideoQualityLow", "Low (faster upload)")}
                </option>
                <option value="medium">
                  {t("liveTrainingVideoQualityMedium", "Medium")}
                </option>
                <option value="high">
                  {t("liveTrainingVideoQualityHigh", "High (sharper)")}
                </option>
              </select>
            </label>

            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-coach-autoeval-dur"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingForcedAutoevalDuration",
                  "Auto-evaluation video duration for clients"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {t(
                  "liveTrainingForcedAutoevalDurationHint",
                  "Last N seconds of each sequence recorded on connected player devices. Default 60 seconds."
                )}
              </span>
              <select
                id="lt-settings-coach-autoeval-dur"
                className="lt-settings-dialog__select"
                value={String(coachDraft.forcedAutoevalVideoMaxDurationSec)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setCoachDraft((prev) =>
                    normalizeCoachSessionSettings({
                      ...prev,
                      forcedAutoevalVideoMaxDurationSec: v,
                    })
                  );
                }}
              >
                {AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS.map((opt) => (
                  <option key={opt} value={String(opt)}>
                    {opt}s
                  </option>
                ))}
              </select>
            </label>
            </div>
            </div>
          </section>
        ) : null}

        <section
          className="lt-settings-dialog__section"
          aria-labelledby="lt-settings-client"
        >
          <h3 id="lt-settings-client" className="lt-settings-dialog__section-title">
            {t("liveTrainingSettingsClient", "This device — player")}
          </h3>

          {clientLocked ? (
            <p className="lt-settings-dialog__forced-banner" role="status">
              {t(
                "liveTrainingCaptureForcedByCoach",
                "The coach locked recording FPS, video quality, and auto-evaluation duration for this session."
              )}
            </p>
          ) : null}

          <FormControlLabel
            className="lt-settings-dialog__switch"
            control={
              <Switch
                checked={clientDraft.cameraRecordingEnabled !== false}
                onChange={(_, checked) =>
                  setClientDraft((prev) =>
                    normalizeClientSettings({
                      ...prev,
                      cameraRecordingEnabled: checked,
                    })
                  )
                }
                color="primary"
              />
            }
            label={
              <span>
                <strong>
                  {t(
                    "liveTrainingPlayerCameraRecording",
                    "Camera recording on this device"
                  )}
                </strong>
                <span className="lt-settings-dialog__hint">
                  {t(
                    "liveTrainingPlayerCameraRecordingHint",
                    "When off, this phone will not open the camera or upload evaluation / match / coach clips. Use a second phone on the same account with this switch on as your camera phone."
                  )}
                </span>
              </span>
            }
          />

          <FormControlLabel
            className="lt-settings-dialog__switch"
            control={
              <Switch
                checked={clientDraft.wifiOnlyVideoUpload !== false}
                onChange={(_, checked) =>
                  setClientDraft((prev) =>
                    normalizeClientSettings({
                      ...prev,
                      wifiOnlyVideoUpload: checked,
                    })
                  )
                }
                color="primary"
              />
            }
            label={
              <span>
                <strong>
                  {t(
                    "liveTrainingWifiOnlyVideoUpload",
                    "Upload videos on Wi‑Fi only"
                  )}
                </strong>
                <span className="lt-settings-dialog__hint">
                  {t(
                    "liveTrainingWifiOnlyVideoUploadHint",
                    "On by default to avoid mobile-data charges. Turn off to allow uploads over cellular. On some phones (especially iPhone) the network type cannot be detected, so uploads may still proceed."
                  )}
                </span>
              </span>
            }
          />

          <label
            className="lt-settings-dialog__field"
            htmlFor="lt-settings-client-camera-view"
          >
            <span className="lt-settings-dialog__label">
              {t("liveTrainingCameraPosition", "Camera position")}
            </span>
            <span className="lt-settings-dialog__hint">
              {t(
                "liveTrainingCameraPositionHint",
                "Where this phone sits relative to the table: spectator (short end) or referee (long side). Stored on each evaluation clip."
              )}
            </span>
            <select
              id="lt-settings-client-camera-view"
              className="lt-settings-dialog__select"
              value={String(clientDraft.cameraView || "spectator")}
              onChange={(e) => {
                setClientDraft((prev) =>
                  normalizeClientSettings({
                    ...prev,
                    cameraView: e.target.value,
                  })
                );
              }}
            >
              {CAMERA_VIEW_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "referee"
                    ? t("liveTrainingCameraReferee", "Referee")
                    : t("liveTrainingCameraSpectator", "Spectator")}
                </option>
              ))}
            </select>
          </label>

          <FormControlLabel
            className="lt-settings-dialog__switch"
            control={
              <Switch
                checked={clientDraft.tablePhoneEnabled === true}
                onChange={(_, checked) =>
                  setClientDraft((prev) =>
                    normalizeClientSettings({
                      ...prev,
                      tablePhoneEnabled: checked,
                      stationPhoneEnabled: checked
                        ? false
                        : prev.stationPhoneEnabled,
                      tablePhoneNumber:
                        prev.tablePhoneNumber ||
                        sessionTables.find((row) => Number(row?.table_number) > 0)
                          ?.table_number ||
                        null,
                    })
                  )
                }
                color="primary"
              />
            }
            label={
              <span>
                <strong>
                  {t(
                    "liveTrainingTablePhone",
                    "Use this phone as the table camera"
                  )}
                </strong>
                <span className="lt-settings-dialog__hint">
                  {t(
                    "liveTrainingTablePhoneHint",
                    "Leave this phone at the tables it films, the same way a club admin iPhone does. It stays there when you move. Each clip is saved for the players seated at every table it records."
                  )}
                </span>
              </span>
            }
          />

          {clientDraft.tablePhoneEnabled ? (
            <>
              <div
                className="lt-settings-dialog__field"
                role="group"
                aria-labelledby="lt-settings-table-phone-label"
              >
                <span
                  id="lt-settings-table-phone-label"
                  className="lt-settings-dialog__label"
                >
                  {t("liveTrainingTablePhoneTables", "Tables this camera records")}
                </span>
                <span className="lt-settings-dialog__hint">
                  {t(
                    "liveTrainingTablePhoneTablesHint",
                    "Pick every table in view. A wide-angle phone can record several tables; the video is available to the players on those tables, and the coach gets a Record button on each one."
                  )}
                </span>
                <div className="lt-settings-dialog__table-chips">
                  {sessionTableNumbers.map((n) => {
                    const on = coveredTables.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        className={`lt-settings-dialog__table-chip${
                          on ? " is-on" : ""
                        }${
                          on && coveredTables.length > 1 && n === mainTable
                            ? " is-main"
                            : ""
                        }`}
                        aria-pressed={on}
                        aria-label={t("table_with_number", { number: n })}
                        onClick={() =>
                          setClientDraft((prev) => {
                            const next = toggleTablePhoneTable(prev, n);
                            // Keep the switch on while the user is still picking.
                            return normalizeClientSettings({
                              ...next,
                              tablePhoneEnabled: true,
                              stationPhoneEnabled: false,
                            });
                          })
                        }
                      >
                        {n}
                      </button>
                    );
                  })}
                  {sessionTableNumbers.length > 1 ? (
                    <button
                      type="button"
                      className="lt-settings-dialog__table-chip lt-settings-dialog__table-chip--all"
                      onClick={() =>
                        setClientDraft((prev) =>
                          normalizeClientSettings({
                            ...setTablePhoneTables(
                              prev,
                              allTablesCovered ? [] : sessionTableNumbers
                            ),
                            tablePhoneEnabled: true,
                            stationPhoneEnabled: false,
                          })
                        )
                      }
                    >
                      {allTablesCovered
                        ? t("liveTrainingCameraTablesNone", "None")
                        : t("liveTrainingCameraTablesAll", "All")}
                    </button>
                  ) : null}
                </div>
                {!coveredTables.length ? (
                  <span className="lt-settings-dialog__hint">
                    {t("liveTrainingTablePhonePick", "Choose a table")}
                  </span>
                ) : null}
              </div>
              {coveredTables.length > 1 ? (
                <label
                  className="lt-settings-dialog__field"
                  htmlFor="lt-settings-table-phone-main"
                >
                  <span className="lt-settings-dialog__label">
                    {t("liveTrainingTablePhoneMainTable", "Main table")}
                  </span>
                  <span className="lt-settings-dialog__hint">
                    {t(
                      "liveTrainingTablePhoneMainTableHint",
                      "The recorded video is shared with the players seated at every selected table. This table is the one shown first."
                    )}
                  </span>
                  <select
                    id="lt-settings-table-phone-main"
                    className="lt-settings-dialog__select"
                    value={mainTable != null ? String(mainTable) : ""}
                    onChange={(e) =>
                      setClientDraft((prev) =>
                        normalizeClientSettings(
                          setTablePhoneMainTable(prev, Number(e.target.value))
                        )
                      )
                    }
                  >
                    {coveredTables.map((n) => (
                      <option key={n} value={String(n)}>
                        {t("table_with_number", { number: n })}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label
                className="lt-settings-dialog__field"
                htmlFor="lt-settings-table-phone-side"
              >
                <span className="lt-settings-dialog__label">
                  {t("liveTrainingTablePhoneSide", "Camera end")}
                </span>
                <select
                  id="lt-settings-table-phone-side"
                  className="lt-settings-dialog__select"
                  value={clientDraft.tablePhoneSide || "A"}
                  onChange={(e) =>
                    setClientDraft((prev) =>
                      normalizeClientSettings({
                        ...prev,
                        tablePhoneSide: e.target.value,
                      })
                    )
                  }
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select>
              </label>
            </>
          ) : null}

          <FormControlLabel
            className="lt-settings-dialog__switch"
            control={
              <Switch
                checked={clientDraft.stationPhoneEnabled === true}
                onChange={(_, checked) =>
                  setClientDraft((prev) =>
                    normalizeClientSettings({
                      ...prev,
                      stationPhoneEnabled: checked,
                      tablePhoneEnabled: checked
                        ? false
                        : prev.tablePhoneEnabled,
                      stationPhoneTsgId:
                        prev.stationPhoneTsgId ||
                        stations.find((s) => s?.tsg_id)?.tsg_id ||
                        null,
                    })
                  )
                }
                color="primary"
              />
            }
            label={
              <span>
                <strong>
                  {t(
                    "liveTrainingStationPhone",
                    "Use this phone as the station camera"
                  )}
                </strong>
                <span className="lt-settings-dialog__hint">
                  {t(
                    "liveTrainingStationPhoneHint",
                    "Link this phone to a whole training station (subgroup), not only one table. The coach can use it for chrono, group clips, and record-on-demand for that station."
                  )}
                </span>
              </span>
            }
          />

          {clientDraft.stationPhoneEnabled ? (
            <label
              className="lt-settings-dialog__field"
              htmlFor="lt-settings-station-phone"
            >
              <span className="lt-settings-dialog__label">
                {t("liveTrainingStationPhoneStation", "Training station")}
              </span>
              <select
                id="lt-settings-station-phone"
                className="lt-settings-dialog__select"
                value={
                  clientDraft.stationPhoneTsgId != null
                    ? String(clientDraft.stationPhoneTsgId)
                    : ""
                }
                onChange={(e) => {
                  const id = e.target.value || null;
                  setClientDraft((prev) =>
                    normalizeClientSettings({
                      ...prev,
                      stationPhoneTsgId: id,
                    })
                  );
                }}
              >
                <option value="">
                  {t("liveTrainingStationPhonePick", "Choose a station")}
                </option>
                {(stations || [])
                  .filter((s) => s?.tsg_id)
                  .map((s) => (
                    <option key={String(s.tsg_id)} value={String(s.tsg_id)}>
                      {s.is_main
                        ? t("mainGroup", "Main group")
                        : s.name || String(s.tsg_id)}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}

          {clientDraft.stationPhoneEnabled && clientDraft.stationPhoneTsgId ? (
            <StationRecordingRoster
              className="lt-station-roster--settings"
              tables={stationRecordingTables}
              t={t}
            />
          ) : null}

          <label
            className={`lt-settings-dialog__field${
              clientLocked ? " lt-settings-dialog__field--disabled" : ""
            }`}
            htmlFor="lt-settings-client-fps"
          >
            <span className="lt-settings-dialog__label">
              {t("liveTrainingRecordingFps", "Video recording FPS")}
            </span>
            <span className="lt-settings-dialog__hint">
              {clientLocked
                ? t(
                    "liveTrainingRecordingFpsForcedHint",
                    "Set by the coach for this session."
                  )
                : t(
                    "liveTrainingRecordingFpsHint",
                    "Preferred capture rate on this device when recording evaluation or coach-requested video."
                  )}
            </span>
            <select
              id="lt-settings-client-fps"
              className="lt-settings-dialog__select"
              disabled={clientLocked}
              value={String(
                clientLocked ? effective.recordingFps : clientDraft.recordingFps
              )}
              onChange={(e) => {
                const v = Number(e.target.value);
                setClientDraft((prev) =>
                  normalizeClientSettings({ ...prev, recordingFps: v })
                );
              }}
            >
              {fpsOptions.map((opt) => (
                <option key={opt} value={String(opt)}>
                  {opt} fps
                </option>
              ))}
            </select>
          </label>

            <label
              className={`lt-settings-dialog__field${
                durationLocked ? " lt-settings-dialog__field--disabled" : ""
              }`}
              htmlFor="lt-settings-autoeval-dur"
            >
              <span className="lt-settings-dialog__label">
                {t(
                  "liveTrainingAutoevalVideoDuration",
                  "Auto-evaluation video duration"
                )}
              </span>
              <span className="lt-settings-dialog__hint">
                {durationLocked
                  ? t(
                      "liveTrainingAutoevalVideoDurationForcedHint",
                      "Set by the coach for this session."
                    )
                  : t(
                      "liveTrainingAutoevalVideoDurationHint",
                      "Maximum length of the clip recorded for self-evaluation after an exercise or sequence. Default 60 seconds."
                    )}
              </span>
              <select
                id="lt-settings-autoeval-dur"
                className="lt-settings-dialog__select"
                disabled={durationLocked}
                value={String(
                  durationLocked
                    ? effective.autoevalVideoMaxDurationSec
                    : clientDraft.autoevalVideoMaxDurationSec
                )}
              onChange={(e) => {
                const v = Number(e.target.value);
                setClientDraft((prev) =>
                  normalizeClientSettings({
                    ...prev,
                    autoevalVideoMaxDurationSec: v,
                  })
                );
              }}
            >
              {AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS.map((opt) => (
                <option key={opt} value={String(opt)}>
                  {opt}s
                </option>
              ))}
            </select>
          </label>

          <label
            className={`lt-settings-dialog__field${
              clientLocked ? " lt-settings-dialog__field--disabled" : ""
            }`}
            htmlFor="lt-settings-client-video-quality"
          >
            <span className="lt-settings-dialog__label">
              {t("liveTrainingVideoQuality", "Evaluation video quality")}
            </span>
            <span className="lt-settings-dialog__hint">
              {clientLocked
                ? t(
                    "liveTrainingVideoQualityForcedHint",
                    "Set by the coach for this session."
                  )
                : t(
                    "liveTrainingVideoQualityHint",
                    "H.264 size/quality for evaluation uploads from this device. Low ≈ 8 MB per minute."
                  )}
            </span>
            <select
              id="lt-settings-client-video-quality"
              className="lt-settings-dialog__select"
              disabled={clientLocked}
              value={String(
                clientLocked
                  ? effective.videoQuality || "low"
                  : clientDraft.videoQuality || "low"
              )}
              onChange={(e) => {
                setClientDraft((prev) =>
                  normalizeClientSettings({
                    ...prev,
                    videoQuality: e.target.value,
                  })
                );
              }}
            >
              <option value="low">
                {t("liveTrainingVideoQualityLow", "Low (faster upload)")}
              </option>
              <option value="medium">
                {t("liveTrainingVideoQualityMedium", "Medium")}
              </option>
              <option value="high">
                {t("liveTrainingVideoQualityHigh", "High (sharper)")}
              </option>
            </select>
          </label>
        </section>
      </DialogContent>
      <DialogActions className="lt-settings-dialog__actions">
        <Button onClick={handleReset} color="inherit" disabled={clientLocked && !isCoachView}>
          {t("reset", "Reset")}
        </Button>
        <Button onClick={onClose}>{t("cancel", "Cancel")}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LiveTrainingSettingsDialog;
